import type React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getPartnerSuggestions,
  clearSuggestions,
} from "../../store/slices/partnersSlice";
import {
  getAddressSuggestions,
  clearSuggestions as clearAddressSuggestions,
} from "../../store/slices/addressesSlice";
import type { PartnerSuggestion } from "../../types";
import { X, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

interface CompanyFilterProps {
  selectedCompanies: PartnerSuggestion[];
  onCompanySelect: (partner: PartnerSuggestion) => void;
  onCompanyRemove: (partnerId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  addresses?: boolean;
}

const CompanyFilter: React.FC<CompanyFilterProps> = ({
  selectedCompanies,
  onCompanySelect,
  onCompanyRemove,
  placeholder = "Search companies...",
  disabled = false,
  error = false,
  className = "",
  addresses = false,
}) => {
  const { t } = useTranslation("shipments");

  const dispatch = useAppDispatch();
  const partnerState = useAppSelector((state) => state.partner);
  const addressState = useAppSelector((state) => state.addresses);

  const { suggestions, loading } = addresses ? addressState : partnerState;

  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const filteredSuggestions = suggestions.filter(
    (partner) =>
      !selectedCompanies.some((selected) => selected._id === partner._id),
  );

  const debouncedSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (term.trim().length >= 2) {
          if (addresses) {
            dispatch(getAddressSuggestions({ query: term.trim(), limit: 10 }));
          } else {
            dispatch(getPartnerSuggestions({ query: term.trim(), limit: 10 }));
          }
          setShowSuggestions(true);
        } else {
          dispatch(clearSuggestions());
          dispatch(clearAddressSuggestions());
          setShowSuggestions(false);
        }
      }, 750);
    },
    [dispatch, addresses],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      dispatch(clearSuggestions());
    };
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setHighlightedIndex(-1);
    debouncedSearch(newValue);
  };

  const handleCompanySelect = (partner: PartnerSuggestion) => {
    onCompanySelect(partner);
    setSearchTerm("");
    setShowSuggestions(false);
    dispatch(clearSuggestions());
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredSuggestions.length
        ) {
          handleCompanySelect(filteredSuggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div ref={wrapperRef} className={`w-full ${className}`}>
      {/* Input */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-dark-300 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={`w-full pl-7 pr-7 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-dark-300 transition-colors
            ${error ? "border-red-300 focus:ring-red-500" : "border-dark-300"}
            ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}
          `}
        />
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary" />
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && searchTerm && filteredSuggestions.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredSuggestions.map((partner, index) => (
            <button
              key={partner._id}
              onClick={() => handleCompanySelect(partner)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors ${
                index === highlightedIndex ? "bg-gray-100" : ""
              }`}
            >
              {partner.companyName}
            </button>
          ))}
        </div>
      )}

      {showSuggestions &&
        filteredSuggestions.length === 0 &&
        !loading &&
        searchTerm.length >= 2 && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-dark-400 text-center">
            {t("filters.noResults")} "{searchTerm}"
          </div>
        )}

      {/* Selected chips */}
      {selectedCompanies.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedCompanies.map((partner) => (
            <div
              key={partner._id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-dark-100 rounded-full text-xs"
            >
              <span className="text-dark-600 font-medium">
                {partner.companyName}
              </span>
              <button
                type="button"
                onClick={() => onCompanyRemove(partner._id)}
                disabled={disabled}
                className="text-dark-400 hover:text-dark-600 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanyFilter;
